import * as Tooltip from '@radix-ui/react-tooltip';
import { motion } from 'framer-motion';
import React, { useState } from 'react';

interface TooltipButtonProps {
  children: React.ReactNode;
  title: React.ReactNode | (() => React.ReactNode);
  showOnClick?: boolean;
  fullWidth?: boolean;
  truncate?: boolean;
  maxWidth?: number | string;
}

// Helper to check if the title is effectively empty
const isTitleEmpty = (val: React.ReactNode): boolean => {
  if (val == null) return true; // matches null or undefined
  if (typeof val === 'string' && val.trim() === '') return true; // empty or whitespace-only strings
  if (Array.isArray(val)) return val.every(isTitleEmpty); // empty arrays or arrays containing only empty values
  return false;
};

const TooltipButton: React.FC<TooltipButtonProps> = ({ children, title, showOnClick = false, fullWidth, truncate = false, maxWidth }) => {
  const [open, setOpen] = useState(false);

  // helper to resolve title only when needed
  const renderTitle = () => (typeof title === 'function' ? (title as () => React.ReactNode)() : title);

  // Resolve the content and determine if we actually have anything to show
  const resolvedTitle = renderTitle();
  const hasContent = !isTitleEmpty(resolvedTitle);

  // Dynamically swap between centering (for icons) and left-alignment (for dropdowns)
  const wrapperClass = truncate
    ? "d-flex align-items-center justify-content-start w-100"
    : "d-flex justify-content-center align-items-center";

  if (showOnClick) {
    return (
      <div
        className={wrapperClass}
        onClick={() => {
          if (hasContent) setOpen((prev) => !prev);
        }}
        style={{ cursor: hasContent ? 'pointer' : 'inherit', position: 'relative', minWidth: truncate ? 0 : 'auto' }}
      >
        <div
          className={truncate ? "text-truncate text-start w-100" : ""}
          style={{
            display: (fullWidth || truncate) ? 'block' : 'inline-block',
            width: (fullWidth || truncate) ? '100%' : 'auto',
            textOverflow: 'ellipsis',
            whiteSpace: fullWidth && !truncate ? 'normal' : 'nowrap',
            verticalAlign: 'middle',
            overflow: truncate ? 'hidden' : 'visible'
          }}
        >
          {children}
        </div>

        {open && hasContent && (
          <motion.div
            className="bg-theme-card text-dark border rounded-1 shadow p-2 text-break position-absolute"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.2 }}
            style={{
              minWidth: '50px',
              maxWidth: maxWidth || '300px',
              textAlign: 'start',
              marginTop: '8px',
              zIndex: 9999,
            }}
          >
            {resolvedTitle}
          </motion.div>
        )}
      </div>
    );
  }

  // If there's no valid title content, render the child structure without the Tooltip logic
  if (!hasContent) {
    return (
      <div className={wrapperClass} style={{ minWidth: truncate ? 0 : 'auto' }}>
        <div
          className={truncate ? "text-truncate text-start w-100" : ""}
          style={{
            display: (fullWidth || truncate) ? "block" : "inline-block",
            width: (fullWidth || truncate) ? "100%" : "auto",
            textOverflow: fullWidth && !truncate ? "clip" : "ellipsis",
            whiteSpace: fullWidth && !truncate ? "normal" : "nowrap",
            verticalAlign: "middle",
            overflow: truncate ? "hidden" : "visible",
            cursor: 'inherit',
          }}
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperClass} style={{ minWidth: truncate ? 0 : 'auto' }}>
      <Tooltip.Provider delayDuration={200}>
        <Tooltip.Root onOpenChange={setOpen}>
          <Tooltip.Trigger asChild>
            <div
              className={truncate ? "text-truncate text-start w-100" : ""}
              style={{
                display: (fullWidth || truncate) ? "block" : "inline-block",
                width: (fullWidth || truncate) ? "100%" : "auto",
                textOverflow: fullWidth && !truncate ? "clip" : "ellipsis",
                whiteSpace: fullWidth && !truncate ? "normal" : "nowrap",
                verticalAlign: "middle",
                overflow: truncate ? "hidden" : "visible",
                cursor: 'pointer',
              }}
            >
              {children}
            </div>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            {open && (
              <Tooltip.Content side="top" align="start" sideOffset={5} style={{ zIndex: 99999 }}>
                <motion.div
                  className="bg-theme-card text-dark border rounded-1 shadow p-2 text-break"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 4 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    minWidth: '50px',
                    maxWidth: maxWidth || '300px',
                    textAlign: 'start',
                  }}
                >
                  {resolvedTitle}
                </motion.div>
                <Tooltip.Arrow width={10} height={6} className="fill-white" />
              </Tooltip.Content>
            )}
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    </div>
  );
};

export default TooltipButton;